import { FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "@/api/client";
import { PlatformLogo } from "@/components/PlatformLogo";
import type { PipelineEvent } from "@/types";

/** Shape of the minimum fields we need from a Hot Offer to prefill the form.
 *  ``id`` is the HotOffer id used for post-publish auto-claim; it is ``null`` when
 *  publishing a source that isn't a catalog HotOffer (e.g. a Fresh ss.ge card),
 *  in which case ``hot_offer_id`` is simply omitted from the run payload. */
export interface PublishableHotOffer {
  id: string | null;
  platform: "myhome" | "ss";
  external_id: string;
  published_url: string;
  title: string | null;
  short_description: string | null;
  cover_photo_url: string | null;
  price: string | null;
  currency: string | null;
  district: string | null;
  rooms: number | null;
  area_sqm: string | null;
  owner_phone: string | null;
}

interface Props {
  offer: PublishableHotOffer;
  hasSsCreds: boolean;
  onClose: () => void;
  onPublished: () => void;
  /** The backend asked the portal before publishing and the source ad is
   *  gone (409 ``listing_inactive``). Nothing was uploaded; the caller can
   *  grey the card out. */
  onListingInactive?: () => void;
}

type Phase = "form" | "running" | "done" | "error";

/** Mirrors ``_MIN_DESCRIPTION_CHARS`` in app/routes/pipeline.py. A body
 *  shorter than this is a truncated generation, not a terse one — and once it's
 *  on the portal neither ss.ge nor MyHome exposes an edit API to take it back. */
const MIN_DESCRIPTION_CHARS = 150;

export function PublishOfferModal({ offer, hasSsCreds, onClose, onPublished, onListingInactive }: Props) {
  const rawDescription = offer.short_description || "";
  const [description, setDescription] = useState(rawDescription);
  const [prepareState, setPrepareState] = useState<
    "idle" | "preparing" | "ready" | "error"
  >("idle");
  // The description used to be invisible: generated in the background and
  // published sight-unseen, which is how truncated one-line bodies reached the
  // portals unnoticed. It's now shown, editable, and floor-checked.
  const [showEditor, setShowEditor] = useState(false);
  const tooShort = description.trim().length < MIN_DESCRIPTION_CHARS;
  const [useSs, setUseSs] = useState(hasSsCreds);
  const [mhPayBalance, setMhPayBalance] = useState(true);
  const [customPriceEnabled, setCustomPriceEnabled] = useState(false);
  const [customPriceUsd, setCustomPriceUsd] = useState<string>(
    offer.price && offer.currency === "USD" ? String(Math.round(Number(offer.price))) : "",
  );
  // Never prefill a masked portal number (MyHome hands back ``551212***``);
  // only a full, dialable value seeds the field.
  const [ownerPhone, setOwnerPhone] = useState(
    offer.owner_phone && !offer.owner_phone.includes("*") ? offer.owner_phone : "",
  );

  const [phase, setPhase] = useState<Phase>("form");
  const [err, setErr] = useState<string | null>(null);
  // Set when the publish was refused because the source listing is no longer
  // on its portal — a different message from a pipeline failure, and no retry.
  const [inactive, setInactive] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState<string>("");
  const [resultIds, setResultIds] = useState<{ mh: string | null; ss: string | null }>({
    mh: null,
    ss: null,
  });
  const esRef = useRef<EventSource | null>(null);

  // Body scroll lock + Esc handler. The parent (HotOffers) re-renders every
  // second for its countdown, which gives us a *new* ``onClose`` reference
  // each tick. If we let the ES cleanup live in this effect, its dep array
  // would churn and the EventSource would be closed mid-stream — which is
  // exactly what was happening: only the first "scrape start" event
  // surfaced, then the stream got killed on the next parent tick.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && phase === "form") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [phase, onClose]);

  // Close the EventSource only when the modal truly unmounts — never on
  // prop-reference churn from the parent's countdown timer.
  useEffect(() => {
    return () => {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, []);

  const logScrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = logScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logs.length]);

  // Runs the *same* generation the bulk-publish flow uses: it looks the offer
  // up in our catalog by its source URL and feeds the structured params + the
  // agent's persona into AGENT_BULK_PROMPT, producing a fresh, publish-ready
  // Georgian body (params-anchored, hard-pinned to Georgian). The en/ru portal
  // boxes are translated from this Georgian "main" body automatically at
  // publish time (the shared pipeline's ``comment_langs``), so there are three
  // languages out — one main Georgian, two background — exactly like a bulk
  // publish. This kicks off automatically when the modal opens (see the effect
  // below), so the user never has to touch the description — they just confirm
  // the owner phone and publish. On failure we fall back to the raw scraped
  // text, which is already seeded into ``description``.
  async function polishDescription() {
    setPrepareState("preparing");
    try {
      const r = await api<{ description: string; source: string }>(
        "/api/gemini/generate-from-url",
        { method: "POST", json: { url: offer.published_url } },
      );
      setDescription(r.description);
      setPrepareState("ready");
    } catch {
      // Nothing usable came back. The backend now refuses to hand over a
      // truncated body rather than quietly falling back to the scraped text,
      // so the agent writes one — open the editor for them.
      setPrepareState("error");
      setShowEditor(true);
    }
  }

  // Auto-polish on open — the description is now generated for the user in the
  // background rather than behind a manual "Polish" button, so the appeared
  // window only asks for the owner phone.
  useEffect(() => {
    polishDescription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setLogs([]);
    setPhase("running");

    try {
      const body: Record<string, unknown> = {
        url: offer.published_url,
        description: description.trim(),
        mh_pay_balance: mhPayBalance,
        use_ss: useSs && hasSsCreds,
      };
      // Only a catalog HotOffer carries an id for post-publish auto-claim; a Fresh
      // ss.ge card has none, so we omit it and the pipeline publishes URL-only.
      if (offer.id) body.hot_offer_id = offer.id;
      if (customPriceEnabled) {
        body.custom_price_enabled = true;
        body.custom_price_usd = Number(customPriceUsd);
      }
      if (ownerPhone.trim()) body.owner_phone = ownerPhone.trim();

      const { job_id } = await api<{ job_id: string }>("/api/run", {
        method: "POST",
        json: body,
      });

      const es = new EventSource(`/api/stream/${job_id}`, { withCredentials: true });
      esRef.current = es;

      es.onmessage = (ev) => {
        const evt: PipelineEvent = JSON.parse(ev.data);
        if (evt.step === "__close__") {
          es.close();
          return;
        }
        if (evt.message) setLogs((ls) => [...ls, evt.message!]);
        if (evt.status === "start" || evt.status === "progress") {
          setCurrentStep(evt.step || "");
        }
        if (evt.step === "finished") {
          if (evt.status === "done") {
            const r = evt.result as
              | { new_listing_id?: string; ss_result?: { application_id?: string } }
              | undefined;
            setResultIds({
              mh: r?.new_listing_id || null,
              ss: r?.ss_result?.application_id || null,
            });
            setPhase("done");
            // Let the user read the summary for a beat, then pull the card
            // out of the feed via the callback.
            setTimeout(onPublished, 400);
          } else if (evt.status === "error") {
            setErr(evt.message || "Pipeline failed");
            setPhase("error");
          }
        }
      };
      es.onerror = () => {
        es.close();
        if (phase === "running") {
          setErr("Connection to server lost. The publish may still be running — check Placements in a minute.");
          setPhase("error");
        }
      };
    } catch (error) {
      const detail =
        error instanceof ApiError && error.body && typeof error.body === "object"
          ? (error.body as { detail?: unknown }).detail
          : undefined;
      const code =
        detail && typeof detail === "object" && !Array.isArray(detail)
          ? (detail as { error?: unknown }).error
          : undefined;
      if (error instanceof ApiError && error.status === 409 && code === "listing_inactive") {
        setInactive(true);
        onListingInactive?.();
      }
      setErr(
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Request failed",
      );
      setPhase("error");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && phase === "form") onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="card w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b border-white/5 flex items-start gap-3">
          {offer.cover_photo_url ? (
            <img
              src={offer.cover_photo_url}
              alt=""
              className="w-14 h-14 rounded-md object-cover bg-white/[0.03] flex-shrink-0"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div className="w-14 h-14 rounded-md bg-white/[0.03]" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <PlatformLogo platform={offer.platform} size="xs" />
              <span className="text-[11px] text-slate-500 font-mono">
                #{offer.external_id}
              </span>
            </div>
            <div className="text-sm font-semibold text-slate-100 truncate">
              {offer.title || "Untitled listing"}
            </div>
            <div className="text-[12px] text-slate-500 flex flex-wrap gap-x-2">
              {offer.price && (
                <span className="text-slate-300 tabular-nums">
                  {offer.currency === "USD"
                    ? "$"
                    : offer.currency === "GEL"
                      ? "₾"
                      : offer.currency
                        ? offer.currency + " "
                        : ""}
                  {Number(offer.price).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              )}
              {offer.district && <span>· {offer.district}</span>}
              {offer.rooms && <span>· {offer.rooms} rm</span>}
              {offer.area_sqm && (
                <span>
                  ·{" "}
                  {Number(offer.area_sqm).toLocaleString(undefined, { maximumFractionDigits: 0 })}{" "}
                  m²
                </span>
              )}
            </div>
          </div>
          {phase === "form" && (
            <button
              type="button"
              onClick={onClose}
              className="text-slate-500 hover:text-slate-300 p-1"
              aria-label="Close"
            >
              ✕
            </button>
          )}
        </div>

        {/* Body */}
        {phase === "form" && (
          <form onSubmit={submit} className="p-5 space-y-4 overflow-y-auto">
            {/* Description is polished automatically in the background — the
                user only confirms the owner phone below. This line just
                surfaces where that generation is. */}
            <div className="flex items-center gap-2 text-[12px] rounded-md bg-white/[0.03] border border-white/5 px-3 py-2">
              {prepareState === "preparing" || prepareState === "idle" ? (
                <>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="animate-spin text-accent-300"
                  >
                    <path d="M21 12a9 9 0 11-3-6.7L21 8" />
                    <path d="M21 3v5h-5" />
                  </svg>
                  <span className="text-slate-300">Polishing description…</span>
                </>
              ) : prepareState === "error" ? (
                <>
                  <span className="text-rose-300/80">
                    ⚠ Couldn't generate a description — write one below to publish.
                  </span>
                  <button
                    type="button"
                    onClick={polishDescription}
                    className="text-slate-400 hover:text-accent-300 underline ml-auto"
                  >
                    retry
                  </button>
                </>
              ) : (
                <>
                  <span className="text-emerald-400/80">✨ Description ready</span>
                  <button
                    type="button"
                    onClick={polishDescription}
                    className="text-slate-500 hover:text-accent-300 ml-auto"
                    title="Regenerate the description"
                  >
                    ↻ regenerate
                  </button>
                </>
              )}
            </div>

            {/* Description — collapsed while everything is fine, forced open
                when generation failed. */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Description
                </span>
                <div className="flex items-center gap-2">
                  {tooShort && (
                    <span className="text-[11px] text-rose-300 tabular-nums">
                      {description.trim().length} / {MIN_DESCRIPTION_CHARS} min
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowEditor((v) => !v)}
                    className="text-[11px] text-slate-500 hover:text-accent-300"
                  >
                    {showEditor ? "hide" : "edit"}
                  </button>
                </div>
              </div>
              {showEditor ? (
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={8}
                  placeholder="Write the listing description…"
                  className={`input w-full resize-y leading-relaxed !text-[13px] ${
                    tooShort ? "!border-rose-500/40" : ""
                  }`}
                />
              ) : (
                <p className="text-[12.5px] text-slate-400 line-clamp-3 whitespace-pre-wrap">
                  {description.trim() || "—"}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mhPayBalance}
                  onChange={(e) => setMhPayBalance(e.target.checked)}
                  className="mt-0.5"
                />
                <div>
                  <div>Auto-pay MyHome</div>
                  <div className="text-[11px] text-slate-500">
                    Pay the listing fee from your wallet balance.
                  </div>
                </div>
              </label>
              <label
                className={`flex items-start gap-2 text-sm cursor-pointer ${
                  hasSsCreds ? "text-slate-300" : "text-slate-600 cursor-not-allowed"
                }`}
              >
                <input
                  type="checkbox"
                  checked={useSs && hasSsCreds}
                  disabled={!hasSsCreds}
                  onChange={(e) => setUseSs(e.target.checked)}
                  className="mt-0.5"
                />
                <div>
                  <div>Also publish to SS.ge</div>
                  <div className="text-[11px] text-slate-500">
                    {hasSsCreds
                      ? "Mirror the listing in parallel."
                      : "Save SS.ge creds in the vault to enable."}
                  </div>
                </div>
              </label>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={customPriceEnabled}
                  onChange={(e) => setCustomPriceEnabled(e.target.checked)}
                />
                Custom price
              </label>
              {customPriceEnabled && (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                    $
                  </span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={customPriceUsd}
                    onChange={(e) => setCustomPriceUsd(e.target.value)}
                    placeholder="85000"
                    className="input pl-6 pr-3 py-1.5 text-sm w-32 mono"
                  />
                </div>
              )}
              <div className="flex-1 min-w-[200px] relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </span>
                <input
                  type="tel"
                  inputMode="tel"
                  value={ownerPhone}
                  onChange={(e) => setOwnerPhone(e.target.value)}
                  placeholder="Owner phone (optional)"
                  className="input pl-9 pr-3 py-1.5 text-sm w-full mono"
                />
              </div>
            </div>

            {err && (
              <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
                {err}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="text-sm px-4 py-2 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-slate-200 border border-white/10"
              >
                Cancel
              </button>
              <button
                type="submit"
                title={
                  tooShort
                    ? `Descriptions must be at least ${MIN_DESCRIPTION_CHARS} characters`
                    : undefined
                }
                disabled={
                  prepareState === "preparing" ||
                  prepareState === "idle" ||
                  tooShort
                }
                className="text-sm px-4 py-2 rounded-md font-medium bg-accent-500/90 hover:bg-accent-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {prepareState === "preparing" || prepareState === "idle"
                  ? "Polishing…"
                  : "Publish & claim"}
              </button>
            </div>
          </form>
        )}

        {phase === "running" && (
          <div className="p-5 flex flex-col min-h-0">
            <div className="flex items-center gap-3 mb-3">
              <span className="relative flex-shrink-0">
                <span className="block w-2.5 h-2.5 rounded-full bg-accent-500" />
                <span className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-accent-500 animate-ping" />
              </span>
              <div>
                <div className="text-sm text-slate-200 font-semibold">Publishing…</div>
                <div className="text-[12px] text-slate-500">
                  Current step: <span className="text-slate-300">{currentStep || "starting"}</span>
                </div>
              </div>
            </div>
            <div
              ref={logScrollRef}
              className="bg-black/30 border border-white/5 rounded-lg p-3 text-[11px] font-mono text-slate-400 overflow-y-auto h-64"
            >
              {logs.length === 0 ? (
                <div className="text-slate-600">Waiting for pipeline output…</div>
              ) : (
                logs.map((l, i) => (
                  <div key={i} className="whitespace-pre-wrap">
                    {l}
                  </div>
                ))
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-2">
              You can close this window safely — the publish runs in the background.
              We'll also save the listing to Placements with reference to this offer.
            </div>
            <div className="flex justify-end mt-3">
              <button
                type="button"
                onClick={onClose}
                className="text-xs px-3 py-1.5 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-slate-300"
              >
                Run in background
              </button>
            </div>
          </div>
        )}

        {phase === "done" && (
          <div className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center justify-center mx-auto mb-3 ring-1 ring-emerald-500/30">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="text-base font-semibold text-emerald-100">Published</div>
            <div className="text-sm text-slate-400 mt-1">
              {resultIds.mh && (
                <div>
                  MyHome ID: <span className="font-mono text-slate-200">{resultIds.mh}</span>
                </div>
              )}
              {resultIds.ss && (
                <div>
                  SS.ge ID: <span className="font-mono text-slate-200">{resultIds.ss}</span>
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-center gap-2">
              <Link
                to="/placements"
                className="text-sm px-4 py-2 rounded-md bg-accent-500/90 hover:bg-accent-500 text-white font-medium"
              >
                View in Placements
              </Link>
              <button
                type="button"
                onClick={onClose}
                className="text-sm px-4 py-2 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-slate-200 border border-white/10"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div className="p-5">
            <div className="text-sm text-rose-200 font-semibold mb-2">
              {inactive ? "Listing is no longer active — not uploaded" : "Publish failed"}
            </div>
            <div className="text-[12px] text-rose-300/90 bg-rose-500/[0.05] border border-rose-500/25 rounded p-2 mb-4">
              {err}
            </div>
            <div className="flex justify-end gap-2">
              {!inactive && (
                <button
                  type="button"
                  onClick={() => setPhase("form")}
                  className="text-sm px-4 py-2 rounded-md bg-accent-500/20 hover:bg-accent-500/30 text-accent-200 border border-accent-500/30"
                >
                  Edit & retry
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="text-sm px-4 py-2 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-slate-200 border border-white/10"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
